import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Ambulance, Phone, MapPin, ArrowLeft, Clock, CheckCircle2 } from "lucide-react";

// Fix default icon issue for CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ambIcon = L.divIcon({
  html: `<div style="background:#dc2626;color:#fff;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 4px 12px rgba(220,38,38,0.5);font-weight:700">🚑</div>`,
  className: "",
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const pickupIcon = L.divIcon({
  html: `<div style="background:#0F766E;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;font-size:12px;font-weight:700">P</div>`,
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const hospitalIcon = L.divIcon({
  html: `<div style="background:#0891B2;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;font-size:12px;font-weight:700">H</div>`,
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      map.fitBounds(points, { padding: [50, 50] });
    }
  }, [map, points]);
  return null;
}

const STAGE_LABEL = {
  en_route_to_pickup: "En route to pickup",
  at_pickup: "At pickup — patient boarding",
  en_route_to_hospital: "En route to hospital",
  arrived: "Arrived at hospital",
};

export default function AmbulanceTracker() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/emergency/ambulance/${id}`);
        if (alive) setB(data);
      } catch {}
    };
    load();
    const iv = setInterval(load, 8000);
    const iv2 = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { alive = false; clearInterval(iv); clearInterval(iv2); };
  }, [id]);

  // Client-side smooth position interpolation
  const currentPos = useMemo(() => {
    if (!b) return null;
    const p = Math.min(Math.max(b.progress || 0, 0), 1);
    const pickup = [b.pickup_lat, b.pickup_lng];
    const dest = [b.destination_lat, b.destination_lng];
    // Ambulance origin (~1km NE of pickup) → pickup → hospital
    const origin = [pickup[0] + 0.008, pickup[1] + 0.008];
    if (p < 0.4) {
      const t = p / 0.4;
      return [origin[0] + (pickup[0] - origin[0]) * t, origin[1] + (pickup[1] - origin[1]) * t];
    }
    if (p < 0.5) return pickup;
    const t = (p - 0.5) / 0.5;
    return [pickup[0] + (dest[0] - pickup[0]) * t, pickup[1] + (dest[1] - pickup[1]) * t];
  }, [b, tick]);

  if (!b) return <div className="p-10 text-center">Loading tracker…</div>;

  const remaining = Math.max(0, Math.round((1 - (b.progress || 0)) * (b.eta_minutes || 12)));
  const pickup = [b.pickup_lat, b.pickup_lng];
  const dest = [b.destination_lat, b.destination_lng];
  const routeLine = b.progress < 0.5 ? [currentPos, pickup] : [pickup, currentPos];
  const dashedFull = b.progress < 0.5 ? [pickup, dest] : [currentPos, dest];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/patient")} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>

      <div className="grid lg:grid-cols-3 gap-6 mt-4">
        <div className="lg:col-span-2">
          <Card className="p-0 rounded-2xl overflow-hidden">
            <div style={{ height: 520 }}>
              <MapContainer center={pickup} zoom={13} style={{ height: "100%", width: "100%" }} data-testid="tracker-map">
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
                />
                <Marker position={pickup} icon={pickupIcon}><Popup>Pickup: {b.pickup_address}</Popup></Marker>
                <Marker position={dest} icon={hospitalIcon}><Popup>Hospital: {b.destination_hospital_name}</Popup></Marker>
                {currentPos && <Marker position={currentPos} icon={ambIcon}><Popup>{b.vehicle_number}</Popup></Marker>}
                <Polyline positions={routeLine} color="#dc2626" weight={4} />
                <Polyline positions={dashedFull} color="#94a3b8" weight={3} dashArray="8" />
                <AutoFit points={[pickup, dest]} />
              </MapContainer>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 rounded-2xl" data-testid="tracker-status">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center text-red-700">
                <Ambulance className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-red-700">{b.ambulance_type?.toUpperCase()} ambulance</div>
                <div className="font-display font-bold">{b.vehicle_number}</div>
              </div>
            </div>
            <Badge className={`mt-3 ${b.stage === "arrived" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"} border-0`}>
              {b.stage === "arrived" ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : null}
              {STAGE_LABEL[b.stage] || b.status}
            </Badge>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 inline-flex items-center gap-1"><Clock className="w-4 h-4" />ETA</span>
                <span className="font-mono font-bold text-lg">{remaining} min</span>
              </div>
              <Progress value={(b.progress || 0) * 100} className="mt-2" />
            </div>
          </Card>

          <Card className="p-5 rounded-2xl" data-testid="tracker-driver">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Driver</div>
            <div className="font-semibold">{b.driver_name}</div>
            <a href={`tel:${b.driver_phone}`} className="mt-2 inline-flex items-center gap-2 text-teal-700 font-semibold" data-testid="call-driver">
              <Phone className="w-4 h-4" /> {b.driver_phone}
            </a>
          </Card>

          <Card className="p-5 rounded-2xl">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Route</div>
            <div className="text-sm"><strong className="text-teal-700">Pickup:</strong> {b.pickup_address}</div>
            <div className="text-sm mt-1"><strong className="text-cyan-700">Drop:</strong> {b.destination_hospital_name || b.drop_address}</div>
            {b.distance_km != null && (
              <div className="text-xs text-slate-500 mt-2">{b.distance_km} km · ₹{b.amount} (₹{b.base_fare} base + ₹{b.per_km_rate}/km)</div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
